import { Link } from 'react-router-dom'
import { ROUTES } from '@/components/gg/chrome'
import { useMoveBudget, useWatchState } from '@/hooks/useAlerts'
import { useSeedBalance } from '@/hooks/useSeeds'
import type { Alert, WatchState } from './types'
import { SEED, asOf } from './ui'
import { useState } from 'react'

/** The alert budget, always on screen in the queue (alerts.html). Tap it for Settings › Breaking. */
export function BudgetBadge({ w, onOpen }: { w: WatchState; onOpen: () => void }) {
  const cls = w.bank === 0 ? ' zero' : w.bank <= w.floor ? ' low' : ''
  return (
    <button type="button" className="gga-wb" onClick={onOpen} title="Breaking news settings" aria-label={`Alert budget ${w.bank} of ${w.cap}`}>
      <div className="k">Alert budget</div>
      <div className={`v${cls}`}>{SEED}<b>{w.bank}</b><small>/ {w.cap}</small></div>
    </button>
  )
}

export function AlertTag({ a }: { a: Pick<Alert, 'subject' | 'condition'> }) {
  return <span className="gga-tag">{a.subject}<span className="d">·</span><span className="c">{a.condition}</span></span>
}

/** A card in the queue: subject · condition, when it broke, the headline, the outlet, Dismiss and Build · 12. */
export function AlertCard({ a, selected, onOpen, onDismiss, onBuild, cost }: {
  a: Alert; selected?: boolean; onOpen: () => void; onDismiss: () => void; onBuild: () => void; cost: number
}) {
  return (
    <div className={`gga-card${selected ? ' sel' : ''}`} role="button" tabIndex={0} onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen() } }}>
      <AlertTag a={a} />{a.test && <span className="gga-test">Test</span>}
      <div className="gga-when">{asOf(a.broke_at)}</div>
      <div className="gga-hl">{a.headline}</div>
      <div className="gga-foot">
        <span className="gga-src">{a.outlet}</span>
        <button type="button" className="gga-btn" onClick={(e) => { e.stopPropagation(); onDismiss() }}>Dismiss</button>
        <button type="button" className="gga-btn gga-btn--b" onClick={(e) => { e.stopPropagation(); onBuild() }}>Build<span className="seed-badge"> · {cost}</span></button>
      </div>
    </div>
  )
}

/**
 * The empty budget (watch-empty.html v1.2): the second touch after the
 * last-seed push. Matching has stopped, nothing is backfilled, the watches are
 * kept; one Refill of a fixed 5 from this week's seeds, no quantity picker.
 */
export function WatchEmpty() {
  const { data: w } = useWatchState()
  const { data: bal } = useSeedBalance()
  const move = useMoveBudget()
  const [filled, setFilled] = useState(0)
  if (!w) return null
  const weekly = bal?.weekly_remaining ?? 0
  const n = Math.min(w.step, w.cap - w.bank, weekly)
  if (w.bank > 0) {
    return (
      <div className="gga-empty">
        <div className="seed" style={{ color: 'var(--amber)' }}>{SEED}</div>
        <div className="n ok">{w.bank}<small> / {w.cap}</small></div>
        <h1>{filled ? 'Refilled. Your watches are back on.' : 'Your alert budget has seeds.'}</h1>
        <p>{filled ? 'Matching started again the moment the seeds landed. Anything that broke while it was empty isn’t coming back.' : 'Matching is on. This screen only shows when the budget runs dry.'}</p>
        <p className="fine"><Link to={ROUTES.breaking}>Watch settings</Link> · <Link to={ROUTES.alerts}>Breaking alerts</Link></p>
      </div>
    )
  }
  return (
    <div className="gga-empty">
      <div className="seed" style={{ color: 'var(--alert)' }}>{SEED}</div>
      <div className="n">0<small> / {w.cap}</small></div>
      <h1>Your alert budget is empty</h1>
      <p>Matching has stopped. Stories that break while it is empty are not backfilled and cannot be recovered. Your watches are kept; refilling reactivates them instantly.</p>
      <button type="button" className="refill" disabled={n <= 0 || move.isPending}
        onClick={() => move.mutate(1, { onSuccess: (got) => setFilled(got) })}>
        Refill <span className="badge">{SEED}{n > 0 ? n : w.step}</span>
      </button>
      <p className="fine">{n > 0
        ? <>From your weekly seeds. Set a custom amount on the <Link to={ROUTES.breaking}>Breaking tab</Link>.</>
        : <>No weekly seeds left this week. They come back Monday; the <Link to={ROUTES.breaking}>Breaking tab</Link> shows the budget.</>}</p>
    </div>
  )
}
